"use client";

import { cn, formatViews } from "@/lib/utils";
import ProfileTooltip from "@/modules/profile/components/tooltip";
import { useChat } from "@/providers/chat-provider";
import { format, formatDistanceStrict } from "date-fns";
import { PinIcon } from "lucide-react";
import Image from "next/image";

type TChatItemRecipient = {
	id: string | undefined;
	name: string | null | undefined;
	image: string | null | undefined;
	username: string | undefined;
	isFounder: boolean | null | undefined;
	isVerified: boolean | null | undefined;
};

type TChatItemProps = {
	uniqueChatId: string;
	logo?: string;
	name?: string;
	isSuggestion?: boolean;
	onlineUsersCount?: number;
	lastMessage?: string;
	timestamp?: Date;
	recipient?: TChatItemRecipient;
	isGroup?: boolean;
};

const ChatItem = ({
	uniqueChatId,
	logo,
	name,
	isSuggestion,
	onlineUsersCount,
	lastMessage,
	timestamp,
	recipient,
	isGroup = false,
}: TChatItemProps) => {
	const { activeChat, isPinnedChat, handleOpenChat, handlePinChat } = useChat();

	const isPinned = isPinnedChat(uniqueChatId);

	return (
		<div
			key={uniqueChatId}
			className={cn(
				"flex w-full items-center justify-between gap-4 rounded-lg p-2 hover:bg-gray-50",
				activeChat?.uniqueChatId === uniqueChatId ? "bg-brand-primary/[0.05]" : "bg-transparent"
			)}
		>
			<button
				type="button"
				className="flex items-center justify-start gap-2"
				onClick={() => handleOpenChat({ uniqueChatId: uniqueChatId })}
			>
				{isGroup ? (
					<div>
						<div
							className={cn(
								"flex h-12 w-12 items-center justify-center rounded-xl font-medium uppercase",
								logo ? "bg-white" : "bg-brand-primary text-white"
							)}
						>
							{logo ? (
								<Image
									src={logo}
									alt={name || "Group Chat"}
									width={480}
									height={480}
									className="h-full w-full rounded-xl object-contain"
								/>
							) : (
								name?.slice(0, 2)
							)}
						</div>
					</div>
				) : (
					<ProfileTooltip
						description=""
						hideFollowButton
						isFounder={recipient?.isFounder}
						title={recipient?.name || "User"}
						isVerified={recipient?.isVerified}
						image={recipient?.image || undefined}
						username={recipient?.username || undefined}
					/>
				)}

				<div className="space-y-1 text-left">
					<div className="flex items-center justify-start gap-1.5">
						<h3 className="font-semibold capitalize text-gray-800">{name || "Group"}</h3>

						{isSuggestion && (
							<span className="rounded bg-brand-primary/10 px-2 py-1 text-xxs font-medium text-brand-primary">
								Suggestion
							</span>
						)}
					</div>

					{onlineUsersCount && onlineUsersCount > 0 ? (
						<p className="line-clamp-1 text-sm font-medium text-gray-600">
							{formatViews(onlineUsersCount)} online members
						</p>
					) : (
						<p
							title={lastMessage || "Start a conversation"}
							className="line-clamp-1 text-sm font-medium text-gray-600"
							dangerouslySetInnerHTML={{
								__html: lastMessage || "Start a conversation",
							}}
						/>
					)}
				</div>
			</button>

			<div className="flex flex-col items-end gap-2">
				<button
					className="rounded-md p-2 hover:bg-gray-200"
					onClick={() => handlePinChat(uniqueChatId)}
				>
					{isPinned ? (
						<PinIcon className="h-4 w-4 rotate-45 fill-brand-primary stroke-brand-primary" />
					) : (
						<PinIcon className="h-4 w-4 rotate-45 text-gray-500" />
					)}
				</button>

				<span
					className="text-xxs font-medium text-gray-600 sm:text-xs"
					title={timestamp ? format(new Date(timestamp), "PPP") : "Timestamp"}
				>
					{timestamp && formatDistanceStrict(new Date(timestamp), new Date())}
				</span>
			</div>
		</div>
	);
};

export default ChatItem;
